import React, { useMemo, SyntheticEvent, ReactText } from "react";
import { useTranslation } from "react-i18next";

import { FilterIcon } from "fe-shared/src/components/Icons";
import { MultiSelectInput } from "fe-shared/src/components";

import { DataGridFilters } from "fe-shared/src/components/DataGrid/modules/types";

import styles from "./InvoiceAdditionalFilters.module.scss";

interface Props {
    filters: DataGridFilters;
    updateFilters: (filters: DataGridFilters) => void;
    supportedFilters?: FilterName[];
}

export enum FilterName {
    ShowAll = "ShowAll",
    PostedToGP = "PostedToGP",
    NotPostedToGP = "NotPostedToGP",
}

const InvoiceAdditionalFilters = (props: Props) => {
    const { filters, updateFilters, supportedFilters } = props;
    const { t } = useTranslation();
    const { isPostedToGP } = filters;
    const postedToGPChecked = isPostedToGP?.value === "true";
    const notPostedToGPChecked = isPostedToGP?.value === "false";
    const showAllChecked = !notPostedToGPChecked && !postedToGPChecked;
    const filtersCount = [
        postedToGPChecked,
        notPostedToGPChecked
    ].filter(Boolean).length;

    const value = useMemo(
        () =>
            [
                {
                    value: FilterName.ShowAll,
                    isChecked: showAllChecked,
                },
                {
                    value: FilterName.PostedToGP,
                    isChecked: postedToGPChecked,
                },
                {
                    value: FilterName.NotPostedToGP,
                    isChecked: notPostedToGPChecked,
                },
            ]
                .filter((o) => o.isChecked)
                .map((o) => o.value),
        [showAllChecked, postedToGPChecked, notPostedToGPChecked]
    );

    const options = useMemo(
        () =>
            Object.values(FilterName)
                .filter((name) =>
                    supportedFilters ? supportedFilters.includes(name) : true
                )
                .map((name) => ({
                    value: name,
                    label: t(`invoices.grid.additionalFilters.${name}`),
                })),
        [supportedFilters, t]
    );

    const placeholder = (
        <>
            <FilterIcon fillColor={null} />
            Filters
            {filtersCount ? ` +${filtersCount}` : ""}
        </>
    );

    const onChange = (
        e: SyntheticEvent,
        values: ReactText[],
        selectedValue: ReactText
    ) => {
        switch (selectedValue) {
            case FilterName.ShowAll:
                if (!value.includes(FilterName.ShowAll)) {
                    updateFilters({
                        isPostedToGP: {
                            type: "textFilter",
                            value: "",
                        },
                    });
                }
                break;
            case FilterName.PostedToGP:
                updateFilters({
                    isPostedToGP: {
                        type: "textFilter",
                        value: postedToGPChecked ? "" : "true"
                    }
                });
                break;
            case FilterName.NotPostedToGP:
                updateFilters({
                    isPostedToGP: {
                        type: "textFilter",
                        value: notPostedToGPChecked ? "" : "false"
                    }
                });
                break;
            default:
        }
    };

    return (
        <MultiSelectInput
            value={value}
            options={options}
            id="additional-filters-multi-select-input"
            onChange={onChange}
            placeholder={placeholder}
            className={styles.select}
        />
    );
};

export default InvoiceAdditionalFilters;